import React, { useState, useEffect, createContext, useContext, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  UserCheck,
  ShoppingBag,
  Package,
  FileText,
  Shield,
  Settings,
  Activity,
  CreditCard,
  ShieldCheck,
  MoreVertical,
  Bell,
  LogOut,
  Menu,
  X,
  Lock,
  Search,
  PanelLeft,
  Command,
  Building2,
  ChevronsUpDown,
  ClipboardList,
  ClipboardCheck,
  Calendar,
  MessageSquare,
  FlaskConical,
  Sparkles,
  ChevronUp,
  ChevronRight,
  CheckCheck,
  Repeat,
  Brain,
  Stethoscope,
  ShoppingCart,
  LucideIcon,
  LayoutDashboard,
  ListChecks,
  Receipt,
  BarChart3,
  Wallet,
  Layers,
} from 'lucide-react';
import CollapsibleNavGroup from '@/components/sidebar/CollapsibleNavGroup';
import WorkspaceToggle, { WorkspaceMode } from '@/components/sidebar/WorkspaceToggle';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { useClinic } from '@/contexts/ClinicContext';
import { usePendingPrescription } from '@/contexts/PendingPrescriptionContext';
import ChangePasswordDialog from '@/components/ChangePasswordDialog';
import SwitchClinicDialog from '@/components/dialog/SwitchClinicDialog';
import { Clinic as ClinicModel } from '@/models/clinic.model';
import { APP_VERSION, getFooterText } from '@/config/version';
import { Breadcrumbs, BreadcrumbLabelProvider } from '@/components/ui/breadcrumb';
import { CommandPalette } from '@/components/CommandPalette';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { BillingSetupBanner } from '@/components/billing/BillingSetupBanner';
import { ProgramAgreementBanner } from '@/components/clinic/ProgramAgreementBanner';
import { TrialBanner } from '@/components/trial/TrialBanner';
import { TrialExpiredModal } from '@/components/trial/TrialExpiredModal';
import PaymentAccountConfigAlert from '@/components/clinic/PaymentAccountConfigAlert';
import { useQuery } from '@tanstack/react-query';
import { billingService } from '@/services/billingService';
import api from '@/services/api';
import { useNotifications } from '@/contexts/NotificationContext';
import { partnerFailureService } from '@/services/partnerFailureService';

const NIMBUS_CLINIC_ID = import.meta.env.VITE_NIMBUS_CLINIC_ID;

// ─── Notification Bell Popover ──────────────────────────────────────────────
const NotificationBell: React.FC<{ className?: string }> = ({ className = 'h-8 w-8' }) => {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const formatTimeAgo = (dateStr: string) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const handleClick = async (notif: typeof notifications[0]) => {
    if (!notif.read) await markAsRead(notif.id);
    if (notif.type === 'PATIENT_MESSAGE_RECEIVED') navigate('/messaging');
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className={`relative ${className}`}>
          <Bell className="h-4 w-4" strokeWidth={2} />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-blue-500 text-[10px] font-medium text-white">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end" sideOffset={8}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-slate-900">Notifications</h3>
            {unreadCount > 0 && (
              <Badge className="bg-blue-100 text-blue-700 border-0 text-xs">
                {unreadCount} new
              </Badge>
            )}
          </div>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-500 hover:text-slate-700" onClick={() => markAllAsRead()}>
              <CheckCheck className="h-3.5 w-3.5 mr-1" />
              Mark all read
            </Button>
          )}
        </div>
        <ScrollArea className="h-[300px]">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center px-4">
              <Bell className="w-10 h-10 text-slate-200 mb-3" />
              <p className="text-sm text-slate-500">No notifications</p>
              <p className="text-xs text-slate-400 mt-1">You're all caught up!</p>
            </div>
          ) : (
            <div className="py-1">
              {notifications.map((notif, index) => (
                <div key={notif.id}>
                  <div
                    className={`px-4 py-3 cursor-pointer transition-colors ${notif.read ? 'bg-white' : 'bg-blue-50/50'} hover:bg-slate-50`}
                    onClick={() => handleClick(notif)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center flex-shrink-0">
                        {notif.type === 'PATIENT_MESSAGE_RECEIVED' ? (
                          <MessageSquare className="w-4 h-4 text-blue-500" />
                        ) : (
                          <Bell className="w-4 h-4 text-slate-500" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm font-medium text-slate-900 truncate">{notif.title}</p>
                          <span className="text-xs text-slate-400 flex-shrink-0">{formatTimeAgo(notif.created_at)}</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{notif.message}</p>
                      </div>
                    </div>
                  </div>
                  {index < notifications.length - 1 && <Separator className="mx-4" />}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
        <div className="px-4 py-3 border-t border-slate-100">
          <Button
            variant="ghost"
            className="w-full justify-center text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            onClick={() => { navigate('/messaging'); setOpen(false); }}
          >
            View Messages
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

// ─── Sidebar Context ────────────────────────────────────────────────────────
export const SidebarContext = createContext<{
  isCollapsed: boolean;
  setIsCollapsed: (value: boolean) => void;
  isMobileOpen: boolean;
  setIsMobileOpen: (value: boolean) => void;
}>({
  isCollapsed: false,
  setIsCollapsed: () => { },
  isMobileOpen: false,
  setIsMobileOpen: () => { },
});

export const useSidebar = () => useContext(SidebarContext);

// ─── Navigation Section Definitions ─────────────────────────────────────────
interface NavItem {
  id: string;
  title: string;
  icon: LucideIcon;
  path: string;
  badgeCount?: number;
  badgeColor?: string;
  isHighlighted?: boolean;
  hideForAdminOnly?: boolean;
  hideForGuided?: boolean;
  showOnlyForGuided?: boolean;
  requiresEmr?: boolean;
  disabledForTrial?: boolean;
  trialDisabledTooltip?: string;
  nimbusOnly?: boolean;
  /** Hide this individual item from non-admin users (clinic_staff, etc.). */
  requiresAdmin?: boolean;
  /**
   * Permission key (e.g. 'patients:view') required to see this item.
   * Admins (isAdmin) and practitioners (isPractitioner) bypass.
   */
  permission?: string;
}

interface NavSection {
  id: string;
  label: string;
  icon: LucideIcon;
  items: NavItem[];
  badgeCount?: number;
  badgeColor?: string;
  requiresAdmin?: boolean;
}

// ─── Page Transition Variants ───────────────────────────────────────────────
// ─── Clinic Layout Component ────────────────────────────────────────────────
const Clinic = () => {
  const { user, logout, selectClinic } = useAuth();
  const { isPractitioner, isAdmin, isClinicStaff, isGuided, isDirect, isTrial, trialExpired, clinic, getClinic, rolePermissions } =
    useClinic();
  const { pendingCount } = usePendingPrescription();
  const { data: billingStatus } = useQuery({
    queryKey: ['billingStatus', clinic?.id],
    queryFn: () => (clinic?.id ? billingService.getBillingStatus(clinic.id) : null),
    enabled: !!clinic?.id && isAdmin,
    staleTime: 1000 * 60 * 5,
  });
  const billingNeedsSetup =
    isAdmin &&
    billingStatus &&
    billingStatus.billingStatus !== 'active' &&
    billingStatus.billingStatus !== 'trialing';

  // Real-time notification connection status
  const { isConnected: wsConnected } = useNotifications();

  // Messaging unread count — polls every 30s (fallback to 120s when WebSocket connected)
  const { data: messagingUnread } = useQuery({
    queryKey: ['messagingUnreadCount'],
    queryFn: async () => {
      const res = await api.get('/messaging/conversations/unread-count');
      return res.data?.unreadCount ?? 0;
    },
    refetchInterval: wsConnected ? 120_000 : 30_000,
    staleTime: 10_000,
    retry: 1,
    refetchOnWindowFocus: true,
  });
  const messagingUnreadCount = typeof messagingUnread === 'number' ? messagingUnread : 0;

  // Partner-API failures count — Nimbus clinic only (Integration Issues tab)
  const isNimbusClinic = Boolean(NIMBUS_CLINIC_ID && clinic?.id === NIMBUS_CLINIC_ID);
  const { data: partnerFailureCount } = useQuery({
    queryKey: ['partnerFailureCount', clinic?.id],
    queryFn: async () => {
      if (!clinic?.id) return 0;
      const res = await partnerFailureService.counts(clinic.id, false);
      return res?.data?.count ?? 0;
    },
    enabled: Boolean(clinic?.id) && isNimbusClinic && isAdmin,
    refetchInterval: wsConnected ? 120_000 : 30_000,
    staleTime: 10_000,
    retry: 1,
    refetchOnWindowFocus: true,
  });
  const partnerFailureUnresolved = typeof partnerFailureCount === 'number' ? partnerFailureCount : 0;
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Workspace mode for dual-role users (both isPractitioner && isAdmin)
  const [workspaceMode, setWorkspaceMode] = useState<WorkspaceMode>(() => {
    try {
      const stored = localStorage.getItem('novamed-workspace-mode');
      if (stored === 'clinical' || stored === 'operations') return stored;
    } catch { /* ignore */ }
    return 'clinical';
  });

  const handleWorkspaceModeChange = (mode: WorkspaceMode) => {
    setWorkspaceMode(mode);
    try { localStorage.setItem('novamed-workspace-mode', mode); } catch { /* ignore */ }
  };
  const [isMobile, setIsMobile] = useState(false);
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
  const [availableClinics, setAvailableClinics] = useState<ClinicModel[]>([]);
  const [isSwitchingClinic, setIsSwitchingClinic] = useState(false);
  const [showSwitchClinicDialog, setShowSwitchClinicDialog] = useState(false);
  const [selectedClinicToSwitch, setSelectedClinicToSwitch] = useState<ClinicModel | null>(null);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();

  // ─── Load available clinics ──────────────────────────────────────────
  useEffect(() => {
    const loadClinics = async () => {
      if (user?.hasMultipleClinics && user?.clinics) {
        setAvailableClinics(user.clinics);
      } else if (user?.hasMultipleClinics && user?.practitionerId) {
        try {
          const { authService } = await import('@/services/authService');
          const response = await authService.getPractitionerClinics();
          if (response.data?.clinics) {
            setAvailableClinics(response.data.clinics);
          }
        } catch (error) {
          console.error('Error loading clinics:', error);
        }
      }
    };
    if (user) loadClinics();
  }, [user]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  // Track mobile state + close on resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (!mobile) setIsMobileOpen(false);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Scroll-to-top listener
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handleScroll = () => setShowScrollTop(el.scrollTop > 300);
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = useCallback(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // ─── User Helpers ─────────────────────────────────────────────────────
  const getUserInitials = () => {
    if (user?.firstName) return user.firstName[0].toUpperCase();
    if (user?.name) return user.name[0].toUpperCase();
    if (user?.email) return user.email[0].toUpperCase();
    return 'U';
  };

  const getDisplayName = () => {
    if (user?.firstName && user?.lastName) return `${user.firstName} ${user.lastName}`;
    if (user?.name) return user.name;
    if (user?.firstName) return user.firstName;
    return 'Admin';
  };

  const handleSignOut = async () => {
    try {
      await logout();
      toast({ title: 'Success', description: 'Successfully signed out!', variant: 'success' });
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      toast({ title: 'Error', description: 'Failed to sign out. Please try again.', variant: 'destructive' });
    }
  };

  // ─── Role Logic ───────────────────────────────────────────────────────
  const isAdminOnly = isAdmin && !isPractitioner && !isClinicStaff;

  const getActiveRoles = () => {
    const roles: Array<{ key: string; label: string; colorClass: string }> = [];
    // Post Phase A every admin has a UserRole pointing at the "Admin" default
    // role, so the isClinicStaff branch will already render a role_name-driven
    // badge (e.g. "Admin"). Only fall back to the static "Clinic Admin" chip
    // when no UserRole badge is going to render in its place.
    if (isAdmin && !isClinicStaff) {
      roles.push({ key: 'clinic_admin', label: 'Clinic Admin', colorClass: 'bg-violet-100 text-violet-800 border-violet-300' });
    }
    if (isPractitioner) roles.push({ key: 'provider', label: 'Provider', colorClass: 'bg-emerald-100 text-emerald-800 border-emerald-300' });
    if (isClinicStaff) roles.push({ key: 'clinic_staff', label: (clinic as { role_name?: string })?.role_name || 'Clinic Staff', colorClass: 'bg-sky-100 text-sky-800 border-sky-300' });
    return roles;
  };

  // ─── Clinic Switcher ──────────────────────────────────────────────────
  const handleSwitchClinicClick = (clinicOption: ClinicModel) => {
    if (clinicOption.id === clinic?.id) return;
    setSelectedClinicToSwitch(clinicOption);
    setShowSwitchClinicDialog(true);
  };

  const handleConfirmSwitchClinic = async () => {
    if (!selectedClinicToSwitch) return;
    try {
      setIsSwitchingClinic(true);
      await selectClinic(selectedClinicToSwitch.id);
      await getClinic(selectedClinicToSwitch.id);
      setShowSwitchClinicDialog(false);
      toast({ title: 'Success', description: 'Clinic switched successfully!', variant: 'success' });
      window.location.reload();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.message || 'Failed to switch clinic.', variant: 'destructive' });
    } finally {
      setIsSwitchingClinic(false);
      setSelectedClinicToSwitch(null);
    }
  };

  // ─── Build Navigation Sections (Role-Adaptive) ────────────────────────
  // Determine effective view mode:
  // - Dual-role (isPractitioner && isAdmin): respect workspaceMode toggle
  // - Practitioner only: always clinical
  // - Admin only (isAdminOnly): always operations
  const isDualRole = isPractitioner && isAdmin;
  const effectiveMode: WorkspaceMode = isDualRole
    ? workspaceMode
    : isAdminOnly
      ? 'operations'
      : 'clinical';

  const buildNavSections = (): NavSection[] => {
    if (effectiveMode === 'operations') {
      // ── Admin / Operations-First View ──────────────────────────────────
      return [
        {
          id: 'home',
          label: 'Home',
          icon: LayoutDashboard,
          items: [
            { id: 'dashboard', title: 'Dashboard', icon: Activity, path: '/' },
          ],
        },
        {
          id: 'patients',
          label: 'Patients',
          icon: Users,
          items: [
            { id: 'patients', title: 'Patients', icon: Users, path: '/patients' },
          ],
        },
        {
          id: 'staff-group',
          label: 'Staff',
          icon: UserCheck,
          items: [
            { id: 'staff-hub', title: 'Staff', icon: UserCheck, path: '/staff' },
          ],
        },
        {
          id: 'financials-group',
          label: 'Financials',
          icon: Receipt,
          items: [
            { id: 'financials-hub', title: 'Financials', icon: Receipt, path: '/financials' },
          ],
        },
        {
          id: 'billing-group',
          label: 'Billing',
          icon: CreditCard,
          items: [
            { id: 'payments', title: 'Payments', icon: CreditCard, path: '/payments' },
            { id: 'invoices', title: 'Invoices', icon: Receipt, path: '/invoices', hideForGuided: true },
            { id: 'products', title: 'Products', icon: Package, path: '/products', hideForGuided: true, disabledForTrial: true, trialDisabledTooltip: 'Available after completing onboarding' },
            { id: 'storefront', title: 'Storefront', icon: ShoppingBag, path: '/storefront', hideForGuided: true },
            { id: 'guided-orders', title: 'Guided Orders', icon: ClipboardCheck, path: '/guided-orders', showOnlyForGuided: true },
            { id: 'subscriptions', title: 'Subscriptions', icon: Repeat, path: '/subscriptions', hideForGuided: true, disabledForTrial: true, trialDisabledTooltip: 'Available after completing onboarding' },
          ],
        },
        {
          id: 'programs-group',
          label: 'Programs',
          icon: Layers,
          items: [
            { id: 'programs-hub', title: 'Programs', icon: Layers, path: '/programs-hub', hideForGuided: true },
          ],
        },
        {
          id: 'intakes',
          label: 'Intakes',
          icon: ClipboardList,
          items: [
            { id: 'intakes', title: isGuided ? 'Enrollments' : 'Intakes', icon: ClipboardList, path: '/intakes', hideForGuided: true },
          ],
        },
        {
          id: 'admin',
          label: 'Admin',
          icon: Shield,
          items: [
            { id: 'partner-failures', title: 'Integration Issues', icon: Activity, path: '/partner-failures', badgeCount: partnerFailureUnresolved, nimbusOnly: true },
            { id: 'settings', title: 'Settings', icon: Settings, path: '/settings', badgeCount: billingNeedsSetup ? 1 : 0, badgeColor: billingNeedsSetup ? 'bg-amber-500' : undefined },
          ],
        },
      ];
    }

    // ── Practitioner / Clinical-First View ─────────────────────────────
    return [
      {
        id: 'home',
        label: 'My Day',
        icon: LayoutDashboard,
        items: [
          { id: 'dashboard', title: 'My Day', icon: Activity, path: '/' },
        ],
      },
      {
        id: 'clinical',
        label: 'Clinical',
        icon: Stethoscope,
        items: [
          { id: 'patients', title: 'Patients', icon: Users, path: '/patients' },
          { id: 'prescriptions', title: 'Prescriptions', icon: FileText, path: isPractitioner ? '/prescriptions?status=pending' : '/prescriptions', badgeCount: isPractitioner ? pendingCount : 0, hideForGuided: true },
          { id: 'calendar', title: 'Calendar', icon: Calendar, path: '/emr/calendar', requiresEmr: false, hideForGuided: true },
          { id: 'messaging', title: 'Messaging', icon: MessageSquare, path: '/messaging', badgeCount: messagingUnreadCount },
          { id: 'labs', title: 'Lab Orders', icon: FlaskConical, path: '/labs', hideForGuided: true },
          { id: 'tasks', title: 'Tasks', icon: ListChecks, path: '/tasks', hideForAdminOnly: true },
          { id: 'intakes', title: isGuided ? 'Enrollments' : 'Intakes', icon: ClipboardList, path: '/intakes', hideForGuided: true },
        ],
      },
      {
        id: 'billing-collapsed',
        label: 'Billing',
        icon: CreditCard,
        items: [
          { id: 'payments', title: 'Payments', icon: CreditCard, path: '/payments' },
          { id: 'invoices', title: 'Invoices', icon: Receipt, path: '/invoices', hideForGuided: true },
          { id: 'products', title: 'Products', icon: Package, path: '/products', hideForGuided: true, disabledForTrial: true, trialDisabledTooltip: 'Available after completing onboarding' },
          { id: 'storefront', title: 'Storefront', icon: ShoppingBag, path: '/storefront', hideForGuided: true },
          { id: 'guided-orders', title: 'Guided Orders', icon: ClipboardCheck, path: '/guided-orders', showOnlyForGuided: true },
          { id: 'subscriptions', title: 'Subscriptions', icon: Repeat, path: '/subscriptions', hideForGuided: true, disabledForTrial: true, trialDisabledTooltip: 'Available after completing onboarding' },
        ],
      },
    ];
  };

  const navSections = buildNavSections();

  // Filter nav items based on clinic settings and roles
  const filteredSections = navSections
    .filter(section => !section.requiresAdmin || isAdmin)
    .map(section => ({
      ...section,
      items: section.items.filter(item => {
        if (item.id === 'intellihealth' && clinic?.clinic_archetype === 'guided') return false;
        if (item.hideForAdminOnly && isAdminOnly) return false;
        if (item.id === 'tasks' && !isPractitioner) return false;
        if (item.hideForGuided && isGuided) return false;
        if (item.showOnlyForGuided && isDirect) return false;
        if (item.requiresEmr) return clinic?.standalone_emr_enabled === true;
        if (item.nimbusOnly && !isNimbusClinic) return false;
        if (item.requiresAdmin && !isAdmin) return false;
        // Permission gate: admins and practitioners bypass. clinic_staff (and any
        // other custom-role user) needs the explicit grant in rolePermissions.
        if (item.permission && !isAdmin && !isPractitioner) {
          if (!rolePermissions?.includes(item.permission)) return false;
        }
        return true;
      }),
    })).filter(section => section.items.length > 0);

  // Sections that should use CollapsibleNavGroup (with expand/collapse)
  const collapsibleSectionIds = new Set(['billing-collapsed']);

  const isPathActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path.split('?')[0]);
  };

  const activeRoles = getActiveRoles();

  // ─── Render Nav Item ──────────────────────────────────────────────────
  const renderNavItem = (item: NavItem) => {
    // Phase 5A: Disabled trial items — show greyed out with tooltip
    if (item.disabledForTrial && isTrial) {
      const Icon = item.icon;
      if (isCollapsed) {
        return (
          <Tooltip key={item.id}>
            <TooltipTrigger asChild>
              <div className="relative flex items-center justify-center rounded-md p-2 opacity-40 cursor-not-allowed">
                <Icon className="h-4 w-4 text-sidebar-foreground/40" strokeWidth={1.5} />
              </div>
            </TooltipTrigger>
            <TooltipContent side="right" className="bg-slate-900 text-white border-slate-800">
              <p className="font-medium">{item.title}</p>
              <p className="text-xs text-slate-400">{item.trialDisabledTooltip}</p>
            </TooltipContent>
          </Tooltip>
        );
      }
      return (
        <Tooltip key={item.id}>
          <TooltipTrigger asChild>
            <div className="relative flex items-center justify-between px-3 py-[7px] rounded-md text-[12px] opacity-40 cursor-not-allowed">
              <div className="flex items-center gap-3 ml-0.5">
                <Icon className="h-4 w-4 flex-shrink-0 text-sidebar-foreground/40" strokeWidth={1.5} />
                <span className="text-sidebar-foreground/40">{item.title}</span>
              </div>
              <Lock className="h-3 w-3 text-sidebar-foreground/30" />
            </div>
          </TooltipTrigger>
          <TooltipContent side="right" className="bg-slate-900 text-white border-slate-800">
            {item.trialDisabledTooltip}
          </TooltipContent>
        </Tooltip>
      );
    }

    const Icon = item.icon;
    const isActive = isPathActive(item.path);
    const isHighlighted = item.isHighlighted;

    // Collapsed sidebar — icon only with tooltip
    if (isCollapsed) {
      return (
        <Tooltip key={item.id}>
          <TooltipTrigger asChild>
            <Link
              to={item.path}
              className={`relative flex items-center justify-center rounded-md p-2 transition-all duration-150
                ${isActive
                  ? isHighlighted
                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md'
                    : 'bg-brand-teal text-white shadow-sm'
                  : isHighlighted
                    ? 'text-blue-400 hover:bg-blue-500/10'
                    : 'text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground'
                }`}
              style={isActive && isHighlighted ? { background: 'linear-gradient(135deg, hsl(20 45% 42%), hsl(20 45% 32%))', boxShadow: '0 4px 12px hsla(20,45%,57%,0.3)' } : {}}
            >
              <Icon className={`h-4 w-4 ${isHighlighted && !isActive ? 'animate-pulse' : ''} ${isActive ? 'text-white' : ''}`} strokeWidth={isActive ? 2.5 : 2} />
              {item.badgeCount !== undefined && item.badgeCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[14px] h-[14px] px-0.5 text-[8px] font-bold rounded-full bg-red-500 text-white ring-2 ring-sidebar">
                  {item.badgeCount > 9 ? '9+' : item.badgeCount}
                </span>
              )}
            </Link>
          </TooltipTrigger>
          <TooltipContent
            side="right"
            className={`font-medium ${isHighlighted ? 'border-blue-500 bg-blue-500 text-white' : 'bg-slate-900 text-white border-slate-800'}`}
          >
            {item.title}
            {isHighlighted && <span className="ml-1 inline-block w-3 h-3 rounded overflow-hidden align-middle"><img src="/intellihealth-logo.png" alt="" className="w-full h-full object-cover" /></span>}
            {item.badgeCount !== undefined && item.badgeCount > 0 && (
              <Badge className="ml-2 bg-orange-500 text-xs text-white hover:bg-orange-600">{item.badgeCount}</Badge>
            )}
          </TooltipContent>
        </Tooltip>
      );
    }

    // Expanded — IntelliHealth highlighted item
    if (isHighlighted) {
      return (
        <Link
          key={item.id}
          to={item.path}
          className={`flex items-center gap-3 rounded-md px-3 py-[7px] text-[12px] font-medium transition-all duration-150
            ${isActive
              ? 'text-white shadow-md'
              : 'hover:bg-white/5'
            }`}
          style={isActive ? { background: 'linear-gradient(135deg, hsl(20 45% 42%), hsl(20 45% 32%))', boxShadow: '0 4px 12px hsla(20,45%,57%,0.3)' } : {}}
        >
          <div className="relative flex-shrink-0">
            <div className="w-4 h-4 rounded overflow-hidden">
              <img src="/intellihealth-logo.png" alt="IntelliHealth" className="w-full h-full object-cover" />
            </div>
            {!isActive && <span className="absolute -right-1 -top-1 h-2 w-2 animate-ping rounded-full" style={{ backgroundColor: 'hsl(20 45% 57%)' }} />}
          </div>
          <span className={`flex-1 ${isActive ? 'text-white' : 'text-[hsl(20_45%_72%)]'}`}>{item.title}</span>
          {!isActive && (
            <span className="rounded-full px-1.5 py-0.5 text-[10px] font-semibold text-white" style={{ background: 'linear-gradient(135deg, hsl(20 45% 50%), hsl(20 45% 38%))' }}>AI</span>
          )}
        </Link>
      );
    }

    // Expanded — standard item
    return (
      <Link
        key={item.id}
        to={item.path}
        className={`relative flex items-center justify-between px-3 py-[7px] rounded-md font-medium transition-all duration-150 text-[12px] group
          ${isActive
            ? 'bg-brand-teal text-white shadow-sm'
            : 'text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground'
          }`}
      >
        <div className="flex items-center gap-3 ml-0.5">
          <Icon className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-white' : 'text-sidebar-foreground/60 group-hover:text-sidebar-foreground'}`} strokeWidth={isActive ? 2.5 : 1.8} />
          <span>{item.title}</span>
        </div>
        {item.badgeCount !== undefined && item.badgeCount > 0 && (
          <span className={`flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold rounded-full text-white ${isActive ? 'bg-white/20' : 'bg-red-500'}`}>
            {item.badgeCount > 99 ? '99+' : item.badgeCount}
          </span>
        )}
      </Link>
    );
  };

  // ─── Render ────────────────────────────────────────────────────────────
  return (
    <SidebarContext.Provider value={{ isCollapsed, setIsCollapsed, isMobileOpen, setIsMobileOpen }}>
      <TooltipProvider delayDuration={0}>
        <div className="flex h-screen bg-background">
          {/* Mobile Overlay */}
          <AnimatePresence>
            {isMobileOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[70] bg-black/50 lg:hidden"
                onClick={() => setIsMobileOpen(false)}
              />
            )}
          </AnimatePresence>

          {/* ─── Dark Sidebar ──────────────────────────────────────────────── */}
          <div
            className={`fixed inset-y-0 left-0 z-[80] flex flex-col bg-sidebar text-sidebar-foreground border-r border-sidebar-border transition-all duration-300 ease-in-out lg:relative
              ${isCollapsed ? 'lg:w-16' : 'lg:w-64'}
              ${isMobileOpen ? 'w-64 translate-x-0' : 'w-64 -translate-x-full lg:translate-x-0'}`}
          >
            {/* Logo Area */}
            <div className={`flex-none border-b border-sidebar-border ${isCollapsed ? 'p-3' : 'p-4'}`}>
              <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'}`}>
                {isCollapsed ? (
                  <img
                    src="/nimbus_logo_white.svg"
                    alt="Nimbus"
                    className="h-7 w-auto cursor-pointer"
                    style={{ maxWidth: '28px', objectFit: 'cover', objectPosition: 'left' }}
                    onClick={() => navigate('/')}
                  />
                ) : (
                  <>
                    <img
                      src="/nimbus_logo_white.svg"
                      alt="Nimbus"
                      className="h-7 w-auto cursor-pointer"
                      onClick={() => navigate('/')}
                    />
                    <button
                      onClick={() => setIsMobileOpen(false)}
                      className="lg:hidden p-1.5 rounded-md hover:bg-sidebar-accent transition-colors"
                    >
                      <X className="h-5 w-5 text-sidebar-foreground/60" />
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Clinic Switcher */}
            <div className={`flex-none border-b border-sidebar-border ${isCollapsed ? 'px-2 py-3' : 'px-3 py-3'}`}>
              {user?.hasMultipleClinics && availableClinics.length > 1 ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className={`flex w-full items-center gap-3 rounded-lg transition-colors outline-none focus:outline-none
                        ${isCollapsed ? 'justify-center p-2' : 'bg-sidebar-accent p-3 hover:bg-sidebar-accent/80'}`}
                    >
                      {isCollapsed ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex h-8 w-full items-center justify-center rounded-md bg-brand-teal/20">
                              <Building2 className="h-4 w-4 text-brand-teal" />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="right" className="border-slate-700 bg-slate-800 text-xs font-medium text-white">
                            {clinic?.name || user?.clinic?.name || 'N/A'}
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <>
                          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-brand-teal/20">
                            <Building2 className="h-4 w-4 text-brand-teal" />
                          </div>
                          <div className="min-w-0 flex-1 text-left" style={{ lineHeight: 1 }}>
                            <span className="block text-[10px] font-medium uppercase tracking-wide text-sidebar-foreground/60">Switch Clinic</span>
                            <p className="truncate text-sm font-semibold text-white">{clinic?.name || user?.clinic?.name || 'N/A'}</p>
                          </div>
                          <ChevronsUpDown className="h-4 w-4 flex-shrink-0 text-sidebar-foreground/60" />
                        </>
                      )}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" side="bottom" sideOffset={4} className="w-56 border-slate-200 bg-white shadow-lg z-[200]">
                    <DropdownMenuLabel className="px-3 py-2 text-xs font-semibold text-slate-500">Switch Clinic</DropdownMenuLabel>
                    <div className="max-h-[240px] overflow-y-auto scrollbar-hide">
                      {availableClinics.map(clinicOption => {
                        const isSelected = clinicOption.id === clinic?.id;
                        return (
                          <DropdownMenuItem
                            key={clinicOption.id}
                            onClick={() => handleSwitchClinicClick(clinicOption)}
                            disabled={isSwitchingClinic || isSelected}
                            className={`px-3 py-2 text-[13px] ${isSelected ? 'cursor-default' : 'cursor-pointer hover:bg-slate-50'}`}
                          >
                            <Building2 className="mr-3 h-4 w-4 flex-shrink-0 text-slate-400" />
                            <span className="flex-1 truncate text-slate-700">{clinicOption.name}</span>
                            {isSelected && <span className="ml-2 text-brand-teal">✓</span>}
                          </DropdownMenuItem>
                        );
                      })}
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <div className={`flex items-center gap-3 rounded-lg ${isCollapsed ? 'justify-center p-2' : 'bg-sidebar-accent p-3'}`}>
                  {isCollapsed ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex h-8 w-full items-center justify-center rounded-md bg-brand-teal/20">
                          <Building2 className="h-4 w-4 text-brand-teal" />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="border-slate-700 bg-slate-800 text-xs font-medium text-white">
                        {clinic?.name || user?.clinic?.name || 'N/A'}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <>
                      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-brand-teal/20">
                        <Building2 className="h-4 w-4 text-brand-teal" />
                      </div>
                      <div className="min-w-0 flex-1" style={{ lineHeight: 1 }}>
                        <span className="block text-[10px] font-medium uppercase tracking-wide text-sidebar-foreground/60">Clinic</span>
                        <p className="truncate text-sm font-semibold text-white">{clinic?.name || user?.clinic?.name || 'N/A'}</p>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Workspace Toggle — only for dual-role users */}
            {isDualRole && (
              <WorkspaceToggle
                mode={workspaceMode}
                onChange={handleWorkspaceModeChange}
                isCollapsed={isCollapsed}
              />
            )}

            {/* Operations mode indicator — shown when dual-role user is in operations view */}
            {isDualRole && effectiveMode === 'operations' && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.15 }}
                className={`flex-none flex items-center gap-2 border-b border-violet-500/20 bg-violet-500/10 ${isCollapsed ? 'justify-center px-2 py-1.5' : 'px-3 py-1.5'}`}
              >
                <div className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-violet-400 animate-pulse" />
                {!isCollapsed && (
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-violet-400">
                    Operations View
                  </span>
                )}
              </motion.div>
            )}

            {/* Navigation Sections */}
            <div className="flex-1 py-3 overflow-y-auto scrollbar-hide">
              {filteredSections.map((section, sectionIdx) => (
                <div key={section.id} className={sectionIdx > 0 ? 'mt-3' : ''}>
                  {collapsibleSectionIds.has(section.id) ? (
                    <CollapsibleNavGroup
                      id={section.id}
                      label={section.label}
                      icon={section.icon}
                      items={section.items}
                      isCollapsed={isCollapsed}
                      defaultExpanded={false}
                      renderItem={renderNavItem}
                    />
                  ) : (
                    <>
                      {/* Section label — hidden when collapsed */}
                      {!isCollapsed && (
                        <div className="px-3 py-2">
                          <span className="text-[11px] font-semibold tracking-wide text-sidebar-foreground/40">
                            {section.label}
                          </span>
                        </div>
                      )}
                      {isCollapsed && sectionIdx > 0 && (
                        <div className="flex justify-center py-1.5">
                          <div className="w-4 h-px bg-sidebar-foreground/10" />
                        </div>
                      )}
                      <nav className="space-y-0.5 px-2">
                        {section.items.map(renderNavItem)}
                      </nav>
                    </>
                  )}
                </div>
              ))}
            </div>

            {/* Sidebar Footer — User Profile */}
            <div className="flex-none">
              {/* Version info */}
              {!isCollapsed && (
                <div className="px-4 pb-2">
                  <p className="text-[10px] text-sidebar-foreground/25">
                    {getFooterText()} | V{APP_VERSION}
                  </p>
                </div>
              )}
              <div className={`border-t border-sidebar-border flex items-center ${isCollapsed ? 'h-[58px] p-2' : 'h-[64px] px-3 py-2'}`}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    {isCollapsed ? (
                      <button className="flex justify-center items-center w-full h-full rounded-md transition-colors hover:bg-sidebar-accent">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src="" />
                          <AvatarFallback className="text-xs font-medium bg-brand-teal text-white">{getUserInitials()}</AvatarFallback>
                        </Avatar>
                      </button>
                    ) : (
                      <button className="flex items-center justify-between w-full p-2 rounded-md transition-colors hover:bg-sidebar-accent group overflow-hidden">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <Avatar className="h-8 w-8 flex-shrink-0 ring-2 ring-sidebar-foreground/10">
                            <AvatarImage src="" />
                            <AvatarFallback className="text-xs font-medium bg-brand-teal text-white">{getUserInitials()}</AvatarFallback>
                          </Avatar>
                          <div className="min-w-0 flex-1 text-left">
                            <p className="font-medium truncate text-sidebar-foreground text-[0.8rem] leading-tight">{getDisplayName()}</p>
                            <p className="truncate text-sidebar-foreground/50 text-[0.7rem] leading-tight">{user?.email || ''}</p>
                          </div>
                        </div>
                        <MoreVertical className="h-4 w-4 flex-shrink-0 ml-1 text-sidebar-foreground/30 group-hover:text-sidebar-foreground/60 transition-colors" />
                      </button>
                    )}
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    side={isMobile ? 'top' : 'right'}
                    className={`w-56 bg-white border border-slate-200 shadow-lg ${isMobile ? 'mb-2' : 'ml-2'}`}
                  >
                    {/* User info header */}
                    <div className="flex items-center gap-3 p-3 border-b border-slate-100">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src="" />
                        <AvatarFallback className="text-sm font-medium bg-brand-teal text-white">{getUserInitials()}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate text-slate-900">{getDisplayName()}</p>
                        <p className="text-xs truncate text-slate-500">{user?.email || ''}</p>
                      </div>
                    </div>

                    {/* Clinic switching in dropdown */}
                    {user?.hasMultipleClinics && availableClinics.length > 1 && (
                      <>
                        <DropdownMenuLabel className="px-3 py-2 text-xs text-slate-500">Switch Clinic</DropdownMenuLabel>
                        <div className="max-h-[240px] overflow-y-auto scrollbar-hide">
                          {availableClinics.map(clinicOption => (
                            <DropdownMenuItem
                              key={clinicOption.id}
                              onClick={() => handleSwitchClinicClick(clinicOption)}
                              disabled={isSwitchingClinic || clinicOption.id === clinic?.id}
                              className="cursor-pointer px-3 py-2 text-[13px] hover:bg-slate-100 focus:bg-slate-100"
                            >
                              <Building2 className="mr-3 h-4 w-4 text-slate-400" />
                              <span className="flex-1 truncate text-slate-700">{clinicOption.name}</span>
                              {clinicOption.id === clinic?.id && <span className="text-brand-teal">✓</span>}
                            </DropdownMenuItem>
                          ))}
                        </div>
                        <DropdownMenuSeparator className="bg-slate-100" />
                      </>
                    )}

                    <div className="py-1">
                      <DropdownMenuItem onClick={() => setShowChangePasswordDialog(true)} className="cursor-pointer px-3 py-2 text-[13px] hover:bg-slate-100 focus:bg-slate-100">
                        <Lock className="h-4 w-4 mr-3 text-slate-500" strokeWidth={1.5} />
                        <span className="text-slate-900">Change Password</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/preferences')} className="cursor-pointer px-3 py-2 text-[13px] hover:bg-slate-100 focus:bg-slate-100">
                        <Settings className="h-4 w-4 mr-3 text-slate-500" strokeWidth={1.5} />
                        <span className="text-slate-900">Preferences</span>
                      </DropdownMenuItem>
                    </div>
                    <DropdownMenuSeparator className="bg-slate-100" />
                    <div className="py-1">
                      <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer px-3 py-2 text-[13px] hover:bg-red-50 focus:bg-red-50">
                        <LogOut className="h-4 w-4 mr-3 text-red-600" strokeWidth={1.5} />
                        <span className="text-red-600">Sign Out</span>
                      </DropdownMenuItem>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>

          {/* ─── Main Content Area ─────────────────────────────────────────── */}
          <BreadcrumbLabelProvider>
            <div className="flex min-w-0 flex-1 flex-col overflow-hidden bg-background">
              {/* Mobile Header */}
              <div className="flex items-center justify-between border-b px-3 py-2.5 lg:hidden">
                <div className="flex items-center gap-2">
                  <button onClick={() => setIsMobileOpen(true)} className="rounded-md p-2 transition-colors hover:bg-muted">
                    <Menu className="h-5 w-5 text-muted-foreground" />
                  </button>
                  <img src="/nimbus_logo.svg" alt="Nimbus" className="h-5 w-auto cursor-pointer" onClick={() => navigate('/')} />
                </div>
                <div className="flex items-center gap-1">
                  {activeRoles.length > 0 && (
                    <div className="mr-1 flex items-center gap-1">
                      {activeRoles.map(role => (
                        <Badge key={role.key} variant="outline" className={`px-1.5 py-0.5 text-[10px] font-medium ${role.colorClass}`}>
                          {role.label}
                        </Badge>
                      ))}
                    </div>
                  )}
                  <NotificationBell />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button type="button" className="h-8 w-8 cursor-pointer rounded-full outline-none focus:outline-none">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src="" />
                          <AvatarFallback className="bg-brand-teal text-xs font-medium text-white">{getUserInitials()}</AvatarFallback>
                        </Avatar>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48 bg-white p-3 shadow-lg">
                      <div className="mb-2 border-b border-slate-100 px-2 py-1.5 pb-2">
                        <p className="truncate text-sm font-medium text-slate-900">{getDisplayName()}</p>
                        <p className="truncate text-xs text-slate-500">{user?.email || ''}</p>
                      </div>
                      {user?.hasMultipleClinics && availableClinics.length > 1 && (
                        <>
                          <DropdownMenuLabel className="px-2 py-1 text-xs text-slate-500">Switch Clinic</DropdownMenuLabel>
                          <div className="max-h-[240px] overflow-y-auto scrollbar-hide">
                            {availableClinics.map(clinicOption => (
                              <DropdownMenuItem
                                key={clinicOption.id}
                                onClick={() => handleSwitchClinicClick(clinicOption)}
                                disabled={isSwitchingClinic || clinicOption.id === clinic?.id}
                                className="cursor-pointer px-2 py-1.5 text-[13px]"
                              >
                                <Building2 className="mr-2 h-4 w-4 text-slate-500" strokeWidth={2} />
                                <span className="flex-1 truncate">{clinicOption.name}</span>
                                {clinicOption.id === clinic?.id && <span className="ml-2 text-brand-teal">✓</span>}
                              </DropdownMenuItem>
                            ))}
                          </div>
                          <DropdownMenuSeparator className="my-2" />
                        </>
                      )}
                      <DropdownMenuItem onClick={() => setShowChangePasswordDialog(true)} className="cursor-pointer text-[13px]">
                        <Lock className="mr-2 h-4 w-4 text-slate-500" strokeWidth={2} /> Change Password
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/preferences')} className="cursor-pointer text-[13px]">
                        <Settings className="mr-2 h-4 w-4 text-slate-500" strokeWidth={2} /> Preferences
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="my-2" />
                      <DropdownMenuItem className="cursor-pointer text-red-600" onClick={handleSignOut} style={{ fontSize: '13px' }}>
                        <LogOut className="mr-2 h-4 w-4" strokeWidth={2} /> Sign Out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {/* Desktop Header */}
              <header className="bg-white border-b border-slate-200 sticky top-0 z-50 hidden lg:block">
                <div className="px-4 py-2.5">
                  <div className="flex items-center justify-between">
                    {/* Left: Toggle + Search */}
                    <div className="flex items-center space-x-3">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 hover:bg-slate-100 text-slate-700"
                        onClick={() => setIsCollapsed(!isCollapsed)}
                      >
                        <PanelLeft className="h-4 w-4" strokeWidth={2} />
                      </Button>
                      <div className="h-5 w-px bg-slate-200" />
                      {/* Search bar — wired to Command Palette */}
                      <button
                        type="button"
                        onClick={() => setCommandPaletteOpen(true)}
                        className="relative flex items-center h-9 pl-9 pr-12 border border-slate-200 rounded-md bg-[#f8f3ed] hover:bg-[#f0ebe4] hover:border-slate-300 transition-colors duration-200 cursor-pointer"
                        style={{ minWidth: '340px' }}
                      >
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" strokeWidth={2} />
                        <span className="text-sm text-slate-400">Search...</span>
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-slate-200 bg-white">
                          <Command className="h-3 w-3 text-slate-400" strokeWidth={2} />
                          <span className="text-xs text-slate-400 font-medium">K</span>
                        </div>
                      </button>
                    </div>

                    {/* Right: Roles + Icons + Avatar */}
                    <div className="flex items-center space-x-1.5">
                      {activeRoles.length > 0 && (
                        <>
                          <div className="flex items-center gap-2">
                            {activeRoles.map(role => (
                              <Badge key={role.key} variant="outline" className={`px-2.5 py-1 text-xs font-medium ${role.colorClass}`}>
                                {role.label}
                              </Badge>
                            ))}
                          </div>
                          <div className="h-5 w-px bg-slate-200 mx-0.5" />
                        </>
                      )}
                      <NotificationBell className="h-8 w-8 hover:bg-slate-100 text-slate-500" />
                      <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-slate-100 text-slate-500" onClick={() => navigate('/settings')}>
                        <Settings className="h-4 w-4" strokeWidth={2} />
                      </Button>
                    </div>
                  </div>
                </div>
              </header>

              {/* Breadcrumbs — hidden on dashboard */}
              {location.pathname !== '/' && (
                <div className="hidden border-b border-slate-100 bg-white px-4 sm:px-6 lg:px-8 py-1.5 lg:block">
                  <Breadcrumbs />
                </div>
              )}

              {/* Page Content with Transitions */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden relative">
                <div className="mx-auto max-w-[2000px] p-4 sm:p-6 lg:p-8">
                  <TrialBanner />
                  <BillingSetupBanner />
                  <ProgramAgreementBanner />
                  <PaymentAccountConfigAlert className="mb-6" />
                  <ErrorBoundary>
                    <Outlet />
                  </ErrorBoundary>
                </div>

                {/* Scroll-to-Top Button */}
                <AnimatePresence>
                  {showScrollTop && (
                    <motion.button
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ duration: 0.15 }}
                      onClick={scrollToTop}
                      className="fixed bottom-6 right-24 z-30 h-10 w-10 rounded-full bg-slate-900 text-white shadow-lg hover:bg-slate-800 flex items-center justify-center transition-colors"
                      aria-label="Scroll to top"
                    >
                      <ChevronUp className="h-5 w-5" />
                    </motion.button>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </BreadcrumbLabelProvider>
        </div>
      </TooltipProvider>

      {/* Command Palette */}
      <CommandPalette open={commandPaletteOpen} onOpenChange={setCommandPaletteOpen} />

      {/* Dialogs */}
      <TrialExpiredModal />
      <ChangePasswordDialog open={showChangePasswordDialog} onOpenChange={setShowChangePasswordDialog} />
      <SwitchClinicDialog
        open={showSwitchClinicDialog}
        onOpenChange={setShowSwitchClinicDialog}
        clinic={selectedClinicToSwitch}
        onConfirm={handleConfirmSwitchClinic}
        isLoading={isSwitchingClinic}
      />
    </SidebarContext.Provider>
  );
};

export default Clinic;
